import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, ChevronDown, ChevronUp, Mail } from 'lucide-react';
import { BlueprintGrid } from '../components/BlueprintGrid';
import { FaqAccordion } from '../components/FaqAccordion';
export function Book() {
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const faqItems = [{
    question: 'What is this conversation?',
    answer: 'It is a 45-minute working conversation to pressure-test what you are seeing, where revenue may be getting stuck, lost, delayed, or missed, and whether there is a real issue or opportunity worth diagnosing. It is not a discovery call disguised as a pitch.'
  }, {
    question: 'What if the issue seems split across multiple teams?',
    answer: 'That is common. A lot of these issues cut across functions. What looks like a sales issue, an operations issue, or a customer success issue is often a breakdown in the seams between them.'
  }, {
    question: 'How do I know if this is really a revenue journey issue?',
    answer: 'Usually some version of this: the wrong customers get sold, leads stall in the middle, handoffs break, delivery inherits shaky promises, churn starts earlier than leadership thinks, pricing or packaging no longer fits cleanly, or expansion keeps getting missed.'
  }, {
    question: 'Is this only about pre-sale issues?',
    answer: 'No. The revenue journey includes what happens before and after the sale — qualification, routing, conversion, handoffs, promise-to-delivery alignment, churn drivers, retention, expansion, pricing, packaging, and the decision rules around them.'
  }, {
    question: 'What happens if the issue is real?',
    answer: 'The next step is usually a Revenue Journey Diagnostic. That is where we map where revenue is being lost, delayed, or missed, identify the highest-leverage point to address first, and decide whether redesign makes sense.'
  }, {
    question: 'What happens if the issue is not structural?',
    answer: 'Then I will tell you. One possible outcome is that you are fine and should keep pushing. Another is that something is real, but it is not the right timing yet.'
  }, {
    question: 'Do you take over execution?',
    answer: 'No. I do not become the long-term escalation path, own day-to-day throughput, or stay in the middle to keep things moving. The work is designed to reduce dependence, not shift it somewhere else.'
  }, {
    question: 'Where does AI fit into this?',
    answer: 'Usually later. AI can help when the workflow, ownership, definitions, and decision rules are clear enough to support it. When those things are still fuzzy, AI usually scales confusion instead of leverage.'
  }];
  return <main className="w-full" data-id="element-309" data-mp="35d95trs0rr1r">
      {/* Hero */}
      <section className="relative py-24 px-6 bg-paper pt-32 overflow-hidden" data-id="element-310" data-mp="30exvlfxtbej0">
        <div className="absolute inset-0 text-seaglass/[0.15]" data-id="element-311" data-mp="30cv935vvc3j1">
          <BlueprintGrid data-id="element-312" data-mp="2wenou1274k33" />
        </div>

        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto text-center relative z-10" data-id="element-313" data-mp="2wbjf6o5yow51">
          <motion.p variants={fadeIn} className="eyebrow mb-6 text-seaglass" data-id="element-314" data-mp="3o5bmv48kp9cj">
            Book an Honest Conversation
          </motion.p>

          <motion.h1 variants={fadeIn} className="text-4xl md:text-6xl font-serif font-semibold mb-6 leading-tight" data-id="element-315" data-mp="3f4411ln8u0zc">
            <span style={{
            color: '#5A6B7C'
          }} data-id="element-316" data-mp="28p35vi61lmtt">
              A 45-minute working conversation
            </span>{' '}
            <span className="text-navy" data-id="element-317" data-mp="3rkbtpuh5v61h">
              to figure out what is really going on.
            </span>
          </motion.h1>

          <motion.div variants={fadeIn} className="text-lg text-text max-w-3xl mx-auto mb-8 space-y-4" data-id="element-318" data-mp="8sd5xygj896">
            <p data-id="element-319" data-mp="3mls9pzm4o4br">This is not a discovery call disguised as a pitch.</p>
            <p data-id="element-320" data-mp="3mjxk47zwvo1c">
              It is a working conversation to pressure-test what you are seeing,
              where revenue may be getting stuck, lost, delayed, or missed, and
              whether there is a real issue or opportunity worth diagnosing.
            </p>
          </motion.div>

          <motion.div variants={fadeIn} className="flex flex-col items-center gap-2 mb-10" data-id="element-321" data-mp="584xoc1uw38l">
            <a href="#calendar" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300" data-id="element-322" data-mp="2pwax10oq9i0k">
              <span data-id="element-323" data-mp="2md2clm0y5cwt">Choose a time</span>
              <ChevronDown className="w-5 h-5" data-id="element-324" data-mp="2mg6l2vlg2uta" />
            </a>
            <p className="text-sm text-text-muted" data-id="element-325" data-mp="2ibqgcxnkhtfn">
              Video call. You do not need to prep.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* What we cover */}
      <section className="py-24 px-6 bg-paper-dark border-t hairline-top" data-id="element-326" data-mp="2rehsz0s6iv8v">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-327" data-mp="23mudrg4a58mf">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-328" data-mp="2dfi906fm34j1">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-329" data-mp="28lnpp43x4rkh">
              What we cover
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-330" data-mp="1fyb6n3wyv1vx">
            <p className="text-body text-text" data-id="element-331" data-mp="1fvxbkv2adpod">
              We use the conversation to get sharper on:
            </p>

            <ul className="space-y-3 pl-1" data-id="element-332" data-mp="3kzdskfjjincx">
              {['what looks broken on the surface', 'where the real issue or missed opportunity may actually sit', 'whether the journey no longer fits how customers are actually moving', 'what may be getting lost before or after the sale', 'whether the issue is worth diagnosing now'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-333" data-mp="321b01qrsa8ad" data-mpi={i}>
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-334" data-mp="361le7qpqc50b" />
                  <span className="text-body text-text" data-id="element-335" data-mp="3b1pm4scknrex">{item}</span>
                </li>)}
            </ul>

            <div className="mt-8 space-y-4" data-id="element-336" data-mp="laufcjq18mdr">
              <p className="text-body text-text" data-id="element-337" data-mp="le8g5nj1snj3">
                You do not need a perfect story before the call.
              </p>
              <p className="text-body text-navy font-medium" data-id="element-338" data-mp="10ff9qxkv0o55">
                You just need enough signal to look at honestly.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Best fit */}
      <section className="py-24 px-6 bg-paper" data-id="element-339" data-mp="qn9k3ui2yit4">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-340" data-mp="mo0r4ruv6oym">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-341" data-mp="cnqfvcse3rm3">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-342" data-mp="7pggepzmu4uo">
              Best fit
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-343" data-mp="3qzh2smgdynt">
            <p className="text-body text-text" data-id="element-344" data-mp="223ps52rb1x5y">This tends to be useful when:</p>

            <ul className="space-y-3 pl-1" data-id="element-345" data-mp="1y3pgfpvlc02l">
              {['demand is being created, but too much revenue is still getting stuck, lost, or missed in the middle', 'sales, delivery, customer success, and operations keep creating friction for each other', 'churn, expansion, pricing, or packaging issues seem more connected than they first appear', 'too much still depends on judgment trapped in people, key employees, or cleanup', 'leadership knows the business should feel cleaner, clearer, or more commercially aligned than it does'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-346" data-mp="1omqet4wcaqy8" data-mpi={i}>
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-347" data-mp="1ool1ann9er4f" />
                  <span className="text-body text-text" data-id="element-348" data-mp="1tmd1s61yzlk8">{item}</span>
                </li>)}
            </ul>
          </motion.div>
        </motion.div>
      </section>

      {/* What you leave with */}
      <section className="py-24 px-6 bg-paper-dark grain" data-id="element-349" data-mp="3nxrv0wu6w34q">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-6xl mx-auto" data-id="element-350" data-mp="1zggmlglrex9">
          <motion.div variants={fadeIn} className="mb-16 text-center" data-id="element-351" data-mp="asjpshk7t5m3">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-352" data-mp="5yfityay8xl0">
              You leave with
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6" data-id="element-353" data-mp="fqlat817bf07">
            {[{
            title: 'Clearer read',
            body: 'A better read on what may actually be happening.'
          }, {
            title: 'Honest assessment',
            body: 'A sharper sense of whether the issue looks structural, situational, opportunistic, or not worth touching right now.'
          }, {
            title: 'Next right step',
            body: 'A real next move: act now, wait, or leave it alone.'
          }, {
            title: 'Less guessing',
            body: 'More confidence about what is worth changing, what is worth testing, and what is not.'
          }].map((item, i) => <motion.div key={i} variants={fadeIn} className="p-8 bg-white border border-seaglass/10" data-id="element-354" data-mp="2bgo8wir5jdnc" data-mpi={i}>
                <div className="flex items-center space-x-3 mb-4" data-id="element-355" data-mp="344k4tl8wd1j2">
                  <Check className="w-6 h-6 text-seaglass flex-shrink-0" data-id="element-356" data-mp="346eq50o2yvno" />
                </div>
                <h3 className="text-xl font-bold text-navy mb-3 leading-tight" data-id="element-357" data-mp="2zbqwyoi4td2r">
                  {item.title}
                </h3>
                <p className="text-sm text-text leading-relaxed" data-id="element-358" data-mp="3rtmk4mg26iae">{item.body}</p>
              </motion.div>)}
          </div>
        </motion.div>
      </section>

      {/* Possible outcomes */}
      <section className="py-24 px-6 bg-paper" data-id="element-359" data-mp="3uekk2626m8hr">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-360" data-mp="3ufvng4rpte8c">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-361" data-mp="d5xmbo8ufnk7">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-362" data-mp="d7ig3bnc32h0">
              Possible outcomes
            </h2>
            <p className="text-lg text-text" data-id="element-363" data-mp="m63tythv1tbg">
              There are only a few honest answers that come out of this
              conversation:
            </p>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-364" data-mp="vwf9ldawpqy6">
            <ul className="space-y-4" data-id="element-365" data-mp="vyrp7jt6zp82">
              <li className="flex items-start space-x-3" data-id="element-366" data-mp="10wbederfhmes">
                <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-367" data-mp="10towomatpqiu" />
                <span className="text-body text-text" data-id="element-368" data-mp="14xmvevw8kzbd">
                  <strong className="font-semibold text-navy" data-id="element-369" data-mp="31x7xpof6not0">
                    You are fine.
                  </strong>{' '}
                  Keep pushing.
                </span>
              </li>
              <li className="flex items-start space-x-3" data-id="element-370" data-mp="3aqkux2ka5ckw">
                <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-371" data-mp="3as5uj6lgblpm" />
                <span className="text-body text-text" data-id="element-372" data-mp="35xrs4k8dgebg">
                  <strong className="font-semibold text-navy" data-id="element-373" data-mp="3knfrhfwzt2wj">
                    Something is real here, but it is not the right timing yet.
                  </strong>{' '}
                  Wait.
                </span>
              </li>
              <li className="flex items-start space-x-3" data-id="element-374" data-mp="3tn2ug65degrp">
                <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-375" data-mp="3pomzng31s43q" />
                <span className="text-body text-text" data-id="element-376" data-mp="1zc7dogk17vdu">
                  <strong className="font-semibold text-navy" data-id="element-377" data-mp="1v9kgs18i7k9s">
                    There is a real issue or opportunity here worth diagnosing.
                  </strong>{' '}
                  Move into a Revenue Journey Diagnostic.
                </span>
              </li>
            </ul>

            <p className="text-body text-navy font-medium" data-id="element-378" data-mp="1p922dp7tnq27">
              If the issue does not look structural or worth pursuing, I will
              tell you.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* If there is something real to pursue */}
      <section className="relative py-20 px-6 bg-navy grain overflow-hidden" data-id="element-379" data-mp="11gbups6ybsxq">
        <div className="absolute inset-0 text-white/[0.05]" data-id="element-380" data-mp="16hj3nuktyy5z">
          <BlueprintGrid data-id="element-381" data-mp="shhyw41g36l8" />
        </div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="relative max-w-3xl mx-auto text-center z-10" data-id="element-382" data-mp="sijd2hdrc976">
          <h2 className="text-4xl md:text-5xl font-serif font-semibold mb-4 leading-tight" data-id="element-383" data-mp="2ru2nz1imfrjh">
            <span style={{
            color: '#BBBFC4'
          }} data-id="element-384" data-mp="rrt9b0kqr0u7">
              If there is something real
            </span>{' '}
            <span className="text-white" data-id="element-385" data-mp="mtjckljdbs6h">to pursue</span>
          </h2>
          <p className="text-lg text-white/80 mb-4" data-id="element-386" data-mp="hslzuoh4nqcd">
            The next step is usually a Revenue Journey Diagnostic.
          </p>
          <p className="text-lg text-white/70 mb-8" data-id="element-387" data-mp="du4p7uyhzxq7">
            That is where we map where revenue is getting stuck, lost, delayed,
            or missed before and after the sale, identify the highest-leverage
            point to address first, and decide whether redesign makes sense.
          </p>
          <a href="#calendar" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300" data-id="element-388" data-mp="3v6gxvjt8kt2s">
            <span data-id="element-389" data-mp="3h8s9w33vh5ti">Choose a time</span>
            <ChevronDown className="w-5 h-5" data-id="element-390" data-mp="3h9tmwd4g82dv" />
          </a>
        </motion.div>
      </section>

      {/* Calendly */}
      <section id="calendar" className="py-24 px-6 bg-paper-dark grain" data-id="element-391" data-mp="1wq814o18vamn">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="max-w-4xl mx-auto" data-id="element-392" data-mp="21p1dgd4sovl8">
          <div className="p-8 bg-paper/30 border border-seaglass/10" data-id="element-393" data-mp="2aoepndkazy9n">
            <iframe className="bg-white w-full" src="https://calendly.com/johnwestley/30-min-meeting?hide_gdpr_banner=1" title="Book an Honest Conversation" style={{
            minWidth: '320px',
            height: '700px'
          }} frameBorder="0" data-id="element-394" data-mp="1e1jn8m16i5kd"></iframe>
          </div>

          <p className="text-center text-sm text-text-muted mt-4" data-id="element-395" data-mp="ud1009zqh28b">
            If the calendar does not load,{' '}
            <a href="https://calendly.com/johnwestley/30-min-meeting" target="_blank" rel="noopener noreferrer" className="text-seaglass hover:text-seaglass-light underline transition-colors duration-200" data-id="element-396" data-mp="za18erk22h2l">
              open it here
            </a>
            .
          </p>

          <div className="mt-12 text-center space-y-4" data-id="element-397" data-mp="2bgecd4ew0s2c">
            <p className="text-text" data-id="element-398" data-mp="27e9h618sjndl">Questions before booking?</p>
            <a href="mailto:john@growththatholds.com" className="inline-flex items-center space-x-2 text-seaglass hover:text-seaglass-light font-medium transition-colors duration-300" data-id="element-399" data-mp="1jmvo1t2tfnrk">
              <Mail className="w-4 h-4" data-id="element-400" data-mp="1sov7st57t5y7" />
              <span data-id="element-401" data-mp="1slpj3f0i0630">john@growththatholds.com</span>
            </a>
            <p className="text-sm text-text-muted" data-id="element-402" data-mp="1nnflyyv7qver">
              If we are not a fit, I will tell you quickly.
            </p>
            <div className="pt-4" data-id="element-403" data-mp="3451zz6n3ztj2">
              <a href="#calendar" className="inline-flex items-center space-x-2 text-sm text-text-muted hover:text-navy transition-colors duration-300" data-id="element-404" data-mp="2z4nr5mdoofjz">
                <ChevronUp className="w-4 h-4" data-id="element-405" data-mp="3bn9o62kvxpco" />
                <span data-id="element-406" data-mp="3bo2tcz4746sp">Back to calendar</span>
              </a>
            </div>
          </div>
        </motion.div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 bg-paper" data-id="element-407" data-mp="32ljxx02qowgh">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-408" data-mp="32mbofwhhc3zw">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-409" data-mp="3gmct7n0v7vkn">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-410" data-mp="3gjqi5dermxxo">
              FAQ
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} data-id="element-411" data-mp="d4cfdr8movz4">
            <FaqAccordion items={faqItems} data-id="element-412" data-mp="d3b2dh81xzer" />
          </motion.div>
        </motion.div>
      </section>
    </main>;
}